'use client';

import React from 'react';
import { Plus, Trash2, AlertCircle } from 'lucide-react';
import type { AllocationDimension, LineItemAllocation } from '@/hooks/useAllocationDimensions';

interface AllocationDimensionEditorProps {
  dimensions: AllocationDimension[];
  allocations: LineItemAllocation[];
  onChange: (allocations: LineItemAllocation[]) => void;
  isDark?: boolean;
  lineTotal?: number;
}

export function AllocationDimensionEditor({
  dimensions,
  allocations,
  onChange,
  isDark = false,
  lineTotal,
}: AllocationDimensionEditorProps) {
  const total = allocations.reduce((sum, a) => sum + (Number(a.percentage) || 0), 0);
  const isValid = allocations.length === 0 || Math.abs(total - 100) < 0.01;
  const hasEmptyValues = allocations.some((a) => !a.dimension_value || a.dimension_value.trim() === '');

  const addAllocation = () => {
    onChange([...allocations, { dimension_key: dimensions[0]?.key || 'job', dimension_value: '', percentage: allocations.length === 0 ? 100 : 0 }]);
  };

  const updateAllocation = (idx: number, field: keyof LineItemAllocation, value: string | number) => {
    onChange(allocations.map((a, i) => (i === idx ? { ...a, [field]: value } : a)));
  };

  const removeAllocation = (idx: number) => {
    onChange(allocations.filter((_, i) => i !== idx));
  };

  const labelClass = `block text-xs font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`;
  const inputClass = `w-full rounded-lg border px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 ${
    isDark ? 'border-gray-600 bg-gray-900 text-gray-100' : 'border-gray-300 bg-white text-gray-900'
  }`;
  const errorInputClass = `w-full rounded-lg border px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-red-500/30 ${
    isDark ? 'border-red-600 bg-gray-900 text-gray-100' : 'border-red-400 bg-white text-gray-900'
  }`;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className={`text-xs font-semibold ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
          Allocation Dimensions
        </span>
        <button
          type="button"
          onClick={addAllocation}
          className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[10px] font-medium text-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900/20"
        >
          <Plus className="h-3 w-3" />
          Add Dimension
        </button>
      </div>

      {allocations.map((alloc, idx) => {
        const dimension = dimensions.find((d) => d.key === alloc.dimension_key);
        return (
          <div key={idx} className="flex items-start gap-2">
            {/* Dimension type */}
            <div className="flex-1">
              <select
                value={alloc.dimension_key}
                onChange={(e) => {
                  const newDim = dimensions.find((d) => d.key === e.target.value);
                  updateAllocation(idx, 'dimension_key', e.target.value);
                  updateAllocation(idx, 'dimension_value', '');
                }}
                className={inputClass}
              >
                {dimensions.map((d) => (
                  <option key={d.key} value={d.key}>{d.label}</option>
                ))}
              </select>
            </div>

            {/* Dimension value */}
            <div className="flex-1">
              {dimension && dimension.values.length > 0 ? (
                <select
                  value={alloc.dimension_value}
                  onChange={(e) => updateAllocation(idx, 'dimension_value', e.target.value)}
                  className={!alloc.dimension_value ? errorInputClass : inputClass}
                >
                  <option value="">— Select —</option>
                  {dimension.values.map((v) => (
                    <option key={v} value={v}>{v}</option>
                  ))}
                </select>
              ) : (
                <input
                  type="text"
                  value={alloc.dimension_value}
                  onChange={(e) => updateAllocation(idx, 'dimension_value', e.target.value)}
                  placeholder="Value"
                  className={!alloc.dimension_value || alloc.dimension_value.trim() === '' ? errorInputClass : inputClass}
                />
              )}
              {!alloc.dimension_value || alloc.dimension_value.trim() === '' ? (
                <p className={`mt-0.5 text-[9px] ${isDark ? 'text-red-400' : 'text-red-500'}`}>Value required</p>
              ) : null}
            </div>

            {/* Percentage */}
            <div className="w-20">
              <div className="relative">
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  value={alloc.percentage}
                  onChange={(e) => updateAllocation(idx, 'percentage', Number(e.target.value))}
                  className={`${inputClass} pr-6`}
                />
                <span className={`absolute right-2 top-1/2 -translate-y-1/2 text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                  %
                </span>
              </div>
              {lineTotal && alloc.percentage > 0 && (
                <div className={`mt-0.5 text-[9px] ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                  R{((lineTotal * alloc.percentage) / 100).toFixed(2)}
                </div>
              )}
            </div>

            {/* Remove */}
            <button
              type="button"
              onClick={() => removeAllocation(idx)}
              className="mt-0.5 inline-flex h-8 w-8 items-center justify-center rounded-lg text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        );
      })}

      {/* Validation */}
      {allocations.length > 0 && hasEmptyValues && (
        <div className={`flex items-center gap-1.5 rounded-lg px-2 py-1 text-xs ${isDark ? 'bg-red-900/20 text-red-300' : 'bg-red-50 text-red-700'}`}>
          <AlertCircle className="h-3 w-3 shrink-0" />
          All dimension values must be filled in
        </div>
      )}
      {allocations.length > 0 && !hasEmptyValues && !isValid && (
        <div className={`flex items-center gap-1.5 rounded-lg px-2 py-1 text-xs ${isDark ? 'bg-amber-900/20 text-amber-300' : 'bg-amber-50 text-amber-700'}`}>
          <AlertCircle className="h-3 w-3 shrink-0" />
          Total: {total.toFixed(1)}% — must equal 100%
        </div>
      )}
      {allocations.length > 0 && !hasEmptyValues && isValid && (
        <div className={`text-[10px] ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>
          ✓ Total: 100%
        </div>
      )}
    </div>
  );
}
